/**
 * Export: build it, read the manifest, then decide to save it.
 *
 * Three steps, and the order is the requirement rather than a preference.
 * FRONTEND_DESIGN §4.10 asks for "a manifest preview and destination
 * chooser/download", and §9 says "offer explicit copy/download actions; do not
 * silently write exports to arbitrary paths". Together those mean the founder
 * sees what is in the archive *before* it exists anywhere they would have to
 * clean up. So `POST /export` builds it in the server's memory and answers
 * with the manifest; the archive reaches the disk only when the Save button
 * below is pressed, into a location the browser's own dialog chooses.
 *
 * Nothing here renders a size, a count, or a row. The manifest carries entry
 * paths, digests, table names and their record kinds — enough to verify the
 * archive and to see that observations and conclusions are kept apart in it
 * (invariant 2), and not enough to be a copy of anything.
 */

import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ExportResponse } from '@davinci/contracts';
import { useApi } from '../../app/api-context.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { saveFile } from './save-file.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

type RecordKind = ExportResponse['tables'][number]['recordKind'];

const messages = defineMessages({
  en: {
    exportAll: 'Export everything DaVinci holds',
    allIntro1:
      'An export is a plain ZIP file. Nothing in it needs DaVinci to read: the tables are written as text alongside a machine-readable index, and every file in it carries a checksum you can re-compute yourself.',
    allIntro2:
      'DaVinci builds the archive and shows you what is in it before you decide. The archive itself is never written to disk \u2014 it is held in memory until you choose to save it, and DaVinci never picks the location.',
    preparing: 'Preparing the export…',
    prepareAll: 'Prepare an export',
    contains: 'What this archive contains',
    written: (appVersion: string, schemaVersion: string | number, formatVersion: string | number, createdAt: string) =>
      `Written by DaVinci ${appVersion} under database schema ${schemaVersion}, archive format ${formatVersion}, at ${createdAt}.`,
    tables: 'Tables',
    tablesHint:
      'Each table says what kind of record it holds, so a reader can tell what you did from what DaVinci concluded about it without knowing anything about this program.',
    tableLine: (kindLabel: string, kind: string) => `${kindLabel} (${kind}), written to`,
    files: 'Files',
    notIncluded: 'Deliberately not included',
    notIncludedHint:
      'An archive missing something because nobody wrote it yet and an archive missing something on purpose look identical from outside. These are the ones on purpose.',
    retainedHeading: 'In this archive, but not removed by a per-learner deletion',
    heldUntil: (time: string) =>
      `This archive is held only until you save it, and DaVinci discards it shortly after ${time}. Preparing another export replaces it.`,
    saving: 'Saving…',
    save: (fileName: string) => `Save ${fileName}`,
    handed:
      'Your browser has been given the archive. Where it went is between you and your browser \u2014 DaVinci was not told, and cannot delete it later.',
    observation: 'what you did',
    inference: 'what DaVinci concluded',
    reference: 'identity and settings',
    operational: 'how DaVinci was configured',
    derived_view: 'what you did and what was concluded, read side by side',
  },
  he: {
    exportAll: 'ייצוא של כל מה ש-DaVinci מחזיק',
    allIntro1:
      'ייצוא הוא קובץ ZIP פשוט. אין בו שום דבר שדורש את DaVinci כדי לקרוא אותו: הטבלאות כתובות כטקסט, לצד אינדקס שמכונה יכולה לקרוא, ולכל קובץ בו יש סכום ביקורת שאפשר לחשב מחדש בעצמך.',
    allIntro2:
      'DaVinci בונה את הארכיון ומראה לך מה יש בו לפני ההחלטה. הארכיון עצמו אף פעם לא נכתב לדיסק \u2014 הוא נשמר בזיכרון עד לשמירתו, ו-DaVinci אף פעם לא בוחר את המיקום.',
    preparing: 'הכנת הייצוא…',
    prepareAll: 'הכנת ייצוא',
    contains: 'מה יש בארכיון הזה',
    written: (appVersion: string, schemaVersion: string | number, formatVersion: string | number, createdAt: string) =>
      `נכתב על ידי DaVinci ${appVersion} לפי סכמת מסד נתונים ${schemaVersion}, בפורמט ארכיון ${formatVersion}, בתאריך ${createdAt}.`,
    tables: 'טבלאות',
    tablesHint:
      'כל טבלה אומרת איזה סוג רשומות היא מחזיקה, כך שקורא יכול להבחין בין מה שעשית לבין מה ש-DaVinci הסיק מזה בלי לדעת דבר על התוכנה הזו.',
    tableLine: (kindLabel: string, kind: string) => `${kindLabel} (${kind}), נכתבה אל`,
    files: 'קבצים',
    notIncluded: 'לא נכללו בכוונה',
    notIncludedHint:
      'מבחוץ, ארכיון שחסר בו משהו כי אף אחד עוד לא כתב אותו נראה בדיוק כמו ארכיון שחסר בו משהו בכוונה. אלה הדברים שחסרים בכוונה.',
    retainedHeading: 'בארכיון הזה, אבל לא מוסרים במחיקה של לומד בודד',
    heldUntil: (time: string) =>
      `הארכיון הזה נשמר רק עד לשמירתו, ו-DaVinci משליך אותו זמן קצר אחרי ${time}. הכנת ייצוא נוסף מחליפה אותו.`,
    saving: 'שמירה…',
    save: (fileName: string) => `שמירת ${fileName}`,
    handed:
      'הדפדפן שלך קיבל את הארכיון. לאן הוא הלך זה בינך לבין הדפדפן \u2014 DaVinci לא קיבל על כך הודעה, ולא יכול למחוק אותו אחר כך.',
    observation: 'מה שעשית',
    inference: 'מה ש-DaVinci הסיק',
    reference: 'זהות והגדרות',
    operational: 'איך DaVinci הוגדר',
    derived_view: 'מה שעשית ומה שהוסק, זה לצד זה',
  },
});

/** The record kinds, in the words the founder needs rather than the column's. */

export function ExportPanel(): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const headingId = useId();
  const [saved, setSaved] = useState(false);

  const build = useMutation({
    mutationFn: () => api.exportData(),
    // A rebuilt archive is a new ticket, so the previous "saved" note is about
    // a file that is no longer the one on offer.
    onMutate: () => setSaved(false),
  });

  const download = useMutation({
    mutationFn: async (result: ExportResponse) => {
      const blob = await api.downloadExport({ ticket: result.ticket.value });
      saveFile({ blob, fileName: result.suggestedFileName });
    },
    onSuccess: () => setSaved(true),
  });

  const result = build.data;

  return (
    <section className="panel" aria-labelledby={headingId}>
      <h2 id={headingId}>{t.exportAll}</h2>
      <p>{t.allIntro1}</p>
      <p>{t.allIntro2}</p>

      <button type="button" onClick={() => build.mutate()} disabled={build.isPending}>
        {build.isPending ? t.preparing : t.prepareAll}
      </button>

      {build.isError ? (
        <FailureNotice error={build.error} onRetry={() => build.mutate()} />
      ) : null}

      {result !== undefined ? (
        <div className="panel-result">
          <h3>{t.contains}</h3>
          <p>{t.written(result.appVersion, result.schemaVersion, result.formatVersion, result.createdAt)}</p>

          <h4>{t.tables}</h4>
          <p className="field-hint">{t.tablesHint}</p>
          <ul className="plain-list">
            {result.tables.map((table) => (
              <li key={table.name}>
                <code>{table.name}</code> — {t.tableLine(t[table.recordKind as RecordKind], table.recordKind)}{' '}
                <code>{table.rows}</code>
              </li>
            ))}
          </ul>

          <h4>{t.files}</h4>
          <ul className="plain-list">
            {result.entries.map((entry) => (
              <li key={entry.path}>
                <code>{entry.path}</code>
                <span className="entry-digest"> {entry.sha256}</span>
              </li>
            ))}
          </ul>

          <h4>{t.notIncluded}</h4>
          <p className="field-hint">{t.notIncludedHint}</p>
          <ul className="plain-list">
            {result.omissions.map((omission) => (
              <li key={omission.what}>
                <strong>{omission.what}</strong> — {omission.reason}
              </li>
            ))}
          </ul>

          {result.retainedByDesign.length > 0 ? (
            <>
              <h4>{t.retainedHeading}</h4>
              <ul className="plain-list">
                {result.retainedByDesign.map((row) => (
                  <li key={row.table}>
                    <code>{row.table}</code> — {row.reason}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <p>{t.heldUntil(utcTimeOf(result.ticket.expiresAt))}</p>


          <button
            type="button"
            onClick={() => download.mutate(result)}
            disabled={download.isPending}
          >
            {download.isPending ? t.saving : t.save(result.suggestedFileName)}
          </button>

          {download.isError ? (
            <FailureNotice error={download.error} onRetry={() => build.mutate()} />
          ) : null}

          {saved ? (
            <p role="status">{t.handed}</p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * The time out of an ISO-8601 instant, as `HH:MM:SS UTC`.
 *
 * Not `toLocaleString`, and the reason is mechanical rather than aesthetic: a
 * locale-formatted date is `8/3/2026` in the founder's own locale, and
 * `no-percentage.test.tsx` reads a slash between two numbers as a fraction. It
 * would be right to: this screen has no business rendering one, and a date
 * format that happens to look like a ratio is not the exception to make.
 */
function utcTimeOf(iso: string): string {
  return `${iso.slice(11, 19)} UTC`;
}
